/**
 * Client-side cache for a just-generated public link's plaintext path.
 *
 * The backend only ever stores/returns the token hash — the plaintext relative path is
 * shown once, at generation time. This cache keeps that plaintext around in the browser
 * (localStorage by default) so the "Copy link" action survives closing/reopening the
 * workspace or reloading the page, instead of forcing a regenerate just to get a copyable
 * URL again. Keyed by offerId. The caller MUST validate `linkId` against the public-link
 * status endpoint's current `linkId` before trusting a cached entry — a cached entry goes
 * stale when the link is regenerated or revoked from another device/tab.
 */

export interface CachedPublicLink {
  linkId: number;
  relativePath: string;
  expiresAt: Date;
}

interface StoredPublicLink {
  offerId: string;
  linkId: number;
  relativePath: string;
  expiresAt: string;
}

function storageKey(offerId: string): string {
  return `ooh:public-link:${offerId}`;
}

/** Returns the cached entry for offerId, or null when absent, malformed, or unparsable. */
export function readCachedPublicLink(
  offerId: string,
  storage: Storage = window.localStorage,
): CachedPublicLink | null {
  const raw = storage.getItem(storageKey(offerId));
  if (raw == null) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredPublicLink>;
    if (
      parsed.offerId !== offerId ||
      typeof parsed.linkId !== 'number' ||
      typeof parsed.relativePath !== 'string' ||
      typeof parsed.expiresAt !== 'string'
    ) {
      return null;
    }
    return {
      linkId: parsed.linkId,
      relativePath: parsed.relativePath,
      expiresAt: new Date(parsed.expiresAt),
    };
  } catch {
    return null;
  }
}

/** Persists the generated link's plaintext path so it survives a reload in this browser. */
export function writeCachedPublicLink(
  offerId: string,
  entry: CachedPublicLink,
  storage: Storage = window.localStorage,
): void {
  const stored: StoredPublicLink = {
    offerId,
    linkId: entry.linkId,
    relativePath: entry.relativePath,
    expiresAt: entry.expiresAt.toISOString(),
  };
  storage.setItem(storageKey(offerId), JSON.stringify(stored));
}

/** Clears the cached entry — called on revoke so a killed link is never resurfaced. */
export function clearCachedPublicLink(
  offerId: string,
  storage: Storage = window.localStorage,
): void {
  storage.removeItem(storageKey(offerId));
}
